import { eq, noop } from "lodash";
import { useEffect, useSyncExternalStore } from "react";

export interface SinkObject<T> {
  next(value: T): void;
  complete?(): void;
}

export type SinkFunction<T> = (value: T) => void;

export type Sink<T> = SinkObject<T> | SinkFunction<T>;

export type Subscription = () => void;

export interface Reactive<T> {
  readonly value: T;
  subscribe(sink: Sink<T>): Subscription;
  map<A>(
    fn: (value: T) => A,
    shortcuit?: (prev: A, next: A) => boolean,
  ): Reactive<A>;
}

export interface ReactiveMut<T> extends Reactive<T> {
  dispatch(next: T): void;
  flush(): void;
}

export class ConstSignal<T> implements Reactive<T> {
  constructor(public readonly value: T) {}

  subscribe(_sink: Sink<T>): Subscription {
    return noop;
  }

  map<A>(
    fn: (value: T) => A,
    shortcuit: (prev: A, next: A) => boolean = eq,
  ): Reactive<A> {
    return new ReactiveMap(this, fn, shortcuit);
  }
}

export class Signal<T> implements ReactiveMut<T> {
  #value: T;
  #subscribed: Set<Sink<T>> = new Set();

  constructor(value: T) {
    this.#value = value;
  }

  protected shortcuit(current: T, next: T): boolean {
    return eq(current, next);
  }

  get value() {
    return this.#value;
  }

  set value(next: T) {
    this.dispatch(next);
  }

  subscribe(sink: Sink<T>): Subscription {
    this.#subscribed.add(sink);
    return () => {
      this.#subscribed.delete(sink);
      if (typeof sink === "object") {
        sink.complete?.();
      }
    };
  }

  dispatch(next: T) {
    if (this.shortcuit(this.#value, next)) return;
    this.#value = next;
    for (const sink of this.#subscribed) {
      if (typeof sink === "function") {
        sink(next);
      } else {
        sink.next(next);
      }
    }
  }

  flush() {
    for (const sink of this.#subscribed) {
      if (typeof sink !== "function") {
        sink.complete?.();
      }
    }
    this.#subscribed.clear();
  }

  map<A>(
    fn: (value: T) => A,
    shortcuit: (prev: A, next: A) => boolean = eq,
  ): Reactive<A> {
    return new ReactiveMap(this, fn, shortcuit);
  }
}

export class ReactiveMap<T, A> implements Reactive<A> {
  private readonly parent: Reactive<T>;
  private readonly fn: (value: T) => A;
  private readonly shortcuit: (prev: A, next: A) => boolean;

  constructor(
    parent: Reactive<T>,
    fn: (value: T) => A,
    shortcuit: (prev: A, next: A) => boolean,
  ) {
    this.parent = parent;
    this.fn = fn;
    this.shortcuit = shortcuit;
  }

  get value() {
    return this.fn(this.parent.value);
  }

  subscribe(sink: Sink<A>): Subscription {
    let last: A = this.value;
    return this.parent.subscribe({
      next: (value) => {
        const next = this.fn(value);
        if (this.shortcuit(last, next)) return;
        last = next;
        if (typeof sink === "object") {
          sink.next(next);
        } else {
          sink(next);
        }
      },
      complete: () => {
        if (typeof sink === "object") {
          sink.complete?.();
        }
      },
    });
  }

  map<B>(
    fn: (value: A) => B,
    shortcuit: (prev: B, next: B) => boolean = eq,
  ): Reactive<B> {
    return new ReactiveMap(
      this.parent,
      (value) => fn(this.fn(value)),
      shortcuit,
    );
  }
}

export type StartTransitionFn = (transition: () => void) => void;

export class TransitionSignal<T> extends Signal<T> {
  #start: StartTransitionFn;

  constructor(value: T, start: StartTransitionFn) {
    super(value);
    this.#start = start;
  }

  override dispatch(next: T): void {
    this.#start(() => super.dispatch(next));
  }

  override flush(): void {
    this.#start(() => super.flush());
  }
}

export function useSignalValue<T>(signal: Reactive<T>): T {
  let current = signal.value;
  return useSyncExternalStore(
    (onStoreChange) => {
      return signal.subscribe({
        next(next) {
          current = next;
          onStoreChange();
        },
      });
    },
    () => current,
    () => current,
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSignalCleanup(...signals: ReactiveMut<any>[]) {
  useEffect(
    () => () => {
      for (const signal of signals) {
        signal.flush();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    signals,
  );
}
